/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ja';

import CustomReserveComplete from '../../api/reserve/customReserveComplete';
import configMastaApi from '../../api/masta/configMasta';
import { useDidMount, useNavigateWithId } from '../../customHooks';
import TopicPath from '../../components/TopicPath';
import {
  INPUT_TYPE_BIRTHDAY,
  INPUT_DATE_FLAG,
  RSV_FLAG_PHONE,
  CANCEL_ANNOUNCE,
} from '../../constants';
import { dispTellStartPeople } from '../../utils';
import MoveTop from '../../CommonParts';

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.locale('ja');

function CustomizeComplete() {
  const navigate = useNavigateWithId();
  const location = useLocation();
  const handleError = useErrorHandler();

  const state = location?.state ?? '';
  const receptionId = state.receptionId ?? '';
  const rsvSetId = state.rsvSetId ?? '';
  const sendType = state.sendType ?? '';

  const [lgCd, setLgCd] = useState('');
  const [checkedRecItems, setCheckedRecItems] = useState([]);
  const [business, setBusiness] = useState({});
  const [rsvNumber, setRsvNumber] = useState('');
  const [rsvDate, setRsvDate] = useState('');
  const [useMethod, setUseMethod] = useState('');
  const [mailAddress, setMailAddress] = useState('');
  const [fromMailAddress, setFromMailAddress] = useState('xxx@xxxxxx.ne.jp');
  const [mailSendTimes, setMailSendTimes] = useState('');
  const [smsSendTimes, setSmsSendTimes] = useState('');
  const [mailUpdateTimes, setMailUpdateTimes] = useState('');
  const [tellUpdateTimes, setTellUpdateTimes] = useState('');
  const [mailResendLimit, setMailResendLimit] = useState('');
  const [smsResendLimit, setSmsResendLimit] = useState('');
  const [mailUpdateLimit, setMailUpdateLimit] = useState('');
  const [tellUpdateLimit, setTellUpdateLimit] = useState('');
  const [contact, setContact] = useState({ mainName: '', mainTel: '', mainMail: '' });
  const [lgName, setLgName] = useState('');
  const [newMailAddress, setNewMailAddress] = useState('');
  const [newTelNum, setNewTelNum] = useState('');
  const [newConfMailAddress, setNewConfMailAddress] = useState('');
  const [numOfAttendees, setNumOfAttendees] = useState(0);
  const [note, setNote] = useState('');
  const [place, setPlace] = useState({ name: '', address: '', subAddress: '', tel: '' });
  const [smsTelNum, setSmsTelNum] = useState('');
  const [rsvFlag, setRsvFlag] = useState(0);
  const [tellStart, setTellStart] = useState(1);
  const {
    name: placeName = '',
    address: placeAddress = '',
    subAddress: placeSubAddress = '',
    tel: placeTel = '',
  } = place;

  //   カスタマイズで登録したデータ
  const [customizeData, setCustomizeData] = useState([]);

  const infoMailRef = useRef(null);
  const infoSmsRef = useRef(null);

  //   受付設定の取得
  const getRsvSetting = async (lgCd) => {
    const setting = await CustomReserveComplete.getRsvSetting(lgCd).catch((err) => {
      window.alert('予約受付設定の取得に失敗しました。');
      handleError(err);
    });
    const {
      mailResendLimit = 0,
      mailUpdateLimit = 0,
      smsResendLimit = 0,
      tellUpdateLimit = 0,
      contact = '',
      lgName,
      tellStart = 0,
    } = setting;
    const { mainName = '', mainTel = '', mainMail = '' } = contact;

    setMailResendLimit(mailResendLimit);
    setSmsResendLimit(smsResendLimit);
    setMailUpdateLimit(mailUpdateLimit);
    setTellUpdateLimit(tellUpdateLimit);
    setContact({ mainName, mainTel, mainMail });
    setLgName(lgName);
    setTellStart(tellStart);
  };

  //   受付データの取得
  const getReception = async (receptionId) => {
    try {
      const getResult = await CustomReserveComplete.getCustomReception(receptionId).catch((err) => {
        window.alert('受付データの取得に失敗しました。');
        handleError(err);
      });

      const {
        lgCd = '',
        // gender = '',
        // usePlaceId = '',
        business = {},
        checkedRecItems = [],
        rsvNumber = '',
        rsvDate = '',
        useMethod = '',
        // mailAddress = '',
        mailInfo = '',
        smsInfo = '',
        numOfAttendees = 0,
        note = '',
        place = '',
        residentsInfo = [],
        rsvFlag = 0,
      } = getResult;

      const { mailAddress = '', mailSendTimes = '', mailUpdateTimes = '' } = mailInfo;

      const { smsTellNum = '', smsSendTimes = '', tellUpdateTimes = '' } = smsInfo;

      setLgCd(lgCd);
      // setGender(gender);
      // setUsePlaceId(usePlaceId);
      setBusiness(business);
      setCheckedRecItems(checkedRecItems);
      setRsvNumber(rsvNumber);
      setRsvDate(rsvDate);
      setUseMethod(useMethod);
      setMailAddress(mailAddress);
      setMailSendTimes(mailSendTimes);
      setSmsSendTimes(smsSendTimes);
      setMailUpdateTimes(mailUpdateTimes);
      setTellUpdateTimes(tellUpdateTimes);
      setNumOfAttendees(numOfAttendees);
      setNote(note);
      setPlace(place);
      setCustomizeData(residentsInfo);
      setSmsTelNum(smsTellNum);
      setRsvFlag(rsvFlag);

      // 予約設定の取得
      getRsvSetting(rsvSetId);
    } catch {
      window.alert('受付情報の取得に失敗しました。');
      navigate(-1);
    }
  };

  //   初回に走る。受付設定のメアドを取得する。
  const init = async () => {
    if (receptionId !== '') await getReception(receptionId);

    const fromMailConf = await configMastaApi.findByConfCd('rsv_from_mailaddress').catch((err) => {
      window.alert('予約受付設定の取得に失敗しました。');
      handleError(err);
    });
    const { value: fromMail = '' } = fromMailConf;

    setFromMailAddress(fromMail);
    // await getGenderList();
    // await getRecItemList();
  };

  useDidMount(() => {
    init();
  });

  //   表示する用件を作成する。
  const displayRecItems = () => {
    const result = [];
    const recItems = checkedRecItems;
    const businessKeys = Object.keys(business);
    for (let i = 0; i < businessKeys.length; i += 1) {
      const nowBusinessKey = businessKeys[i];
      const { businessName = '' } = business[nowBusinessKey];
      const targetRecItems = recItems.filter(
        (item) => parseInt(item.businessId, 10) === parseInt(nowBusinessKey, 10)
      );

      const recItemKeys = Object.keys(targetRecItems);
      if (recItemKeys.length > 0) {
        result.push(<h3 key={businessName}>{businessName}</h3>);
      }

      const tmpItems = {};
      for (let j = 0; j < recItemKeys.length; j += 1) {
        const nowRecItem = recItemKeys[j];
        const { groupId, groupName, recItemId, recItemName } = targetRecItems[nowRecItem];
        if (!tmpItems[groupId]) {
          tmpItems[groupId] = { groupName, items: [] };
        }

        tmpItems[groupId].items[recItemId] = (
          <li style={{ marginLeft: '24px' }} key={`item_${j}`}>
            {recItemName}
          </li>
        );
      }

      const items = [];
      Object.keys(tmpItems).map((e) => {
        items.push(<strong key={`item_groupName_${i}`}>■{tmpItems[e].groupName}</strong>);
        items.push(<ul key={`item${i}`}>{tmpItems[e].items}</ul>);
      });

      result.push(items);
    }

    return result;
  };

  // メールの再送
  const onClickResendMail = async () => {
    if (mailSendTimes < mailResendLimit) {
      try {
        const { msg = '' } = await CustomReserveComplete.resendMail(receptionId);
        if (msg !== '') {
          window.alert(msg);
          return;
        }
        await getReception(receptionId);
        window.alert('メールを再送しました。');
      } catch (err) {
        window.alert('メールの再送に失敗しました。');
      }
    } else {
      window.alert(
        `メール再送可能制限に達しました。届かない場合はWEB窓口を利用できません。\n市町村窓口にご連絡ください。\n${lgName}${contact.mainName}　${contact.mainTel}\nメールアドレス：${contact.mainMail}`
      );
    }
  };

  //   メアドのバリデーション
  const validationMail = () => {
    let message = '';
    // メールアドレスが区切り文字でつながっていないかを確認
    const separatorPattern = /[,:;、：；\s\u3000]/;
    if (separatorPattern.test(newMailAddress)) {
      message +=
        '複数のメールアドレスは入力できません。区切り文字（, や ;）を削除し、1件のみ入力してください。\n';
    }

    if (!newMailAddress.match(/\S/g)) {
      message += 'メールアドレスを入力してください。\n';
    } else if (newMailAddress.length > 256) {
      message += 'メールアドレスは256文字以内で入力してください。\n';
    } else {
      // @ または . がないと形式エラー
      const test = new RegExp(/^[a-zA-Z0-9!-/:-@¥[-`{-~]*$/);
      if (
        !newMailAddress.includes('@') ||
        !newMailAddress.includes('.') ||
        !test.test(newMailAddress)
      ) {
        message += 'メールアドレスの形式が正しくありません。\n';
      }
    }

    if (!newConfMailAddress.match(/\S/g)) {
      message += 'メールアドレス(確認)を入力してください。\n';
    }

    if (
      newMailAddress.match(/\S/g) &&
      newConfMailAddress.match(/\S/g) &&
      newMailAddress !== newConfMailAddress
    ) {
      message += 'メールアドレスが確認用メールアドレスと異なります。\n';
    }

    // メッセージがあれば、アラートを表示
    if (message.length > 0) {
      window.alert(message);
      return false;
    }

    return true;
  };

  // SMS再送
  const onClickResendSms = async () => {
    if (smsSendTimes < smsResendLimit) {
      try {
        // 再送する受付データの取得

        const { msg = '' } = await CustomReserveComplete.resendSms(receptionId);
        if (msg !== '') {
          window.alert(msg);
          return;
        }
        await getReception(receptionId);
        window.alert('SMSを再送しました。');
      } catch (err) {
        window.alert('SMSの再送に失敗しました。');
      }
    } else {
      window.alert(
        `SMS再送可能制限に達しました。届かない場合はWEB窓口を利用できません。\n市町村窓口にご連絡ください。\n${lgName}${contact.mainName}　${contact.mainTel}\nメールアドレス：${contact.mainMail}`
      );
    }
  };

  // 携帯電話番号のチェック
  const validationSms = () => {
    let message = '';

    const regax = new RegExp(/^0[789]0-?[0-9]{4}-?[0-9]{4}$/);
    if ((sendType === 2 || sendType === 3) && !regax.test(newTelNum)) {
      message += 'SMSを利用する場合は携帯電話の番号を入力してください';
    }

    // メッセージがあれば、アラートを表示

    if (message.length > 0) {
      window.alert(message);
      return false;
    }

    return true;
  };

  // メールの更新
  const onClickUpdateMail = async () => {
    // メールアドレスのバリデーションチェック

    const resultValidate = validationMail();
    if (resultValidate === false) return;

    if (mailUpdateTimes < mailUpdateLimit) {
      try {
        const { msg = '' } = await CustomReserveComplete.updateMail(receptionId, newMailAddress);
        if (msg !== '') {
          window.alert(msg);
          return;
        }

        await getReception(receptionId);
        setNewMailAddress('');
        setNewConfMailAddress('');
        window.alert('メールを再送しました。');
      } catch (err) {
        window.alert('メールアドレスの変更に失敗しました。');
      }
    } else {
      window.alert(
        `メールアドレス変更可能制限に達しました。メールアドレスを変更できません。\n市町村窓口にご連絡ください。\n${lgName}${contact.mainName}　${contact.mainTel}\nメールアドレス：${contact.mainMail}`
      );
    }
  };

  // smsのバリデーションチェック
  const onClickUpdateSms = async () => {
    const resultValidate = validationSms();
    if (resultValidate === false) return;

    if (tellUpdateTimes < tellUpdateLimit) {
      try {
        const { msg = '' } = await CustomReserveComplete.updateSms(receptionId, newTelNum);
        if (msg !== '') {
          window.alert(msg);
          return;
        }

        await getReception(receptionId);
        setNewTelNum('');
        window.alert('SMSを再送しました。');
      } catch (err) {
        window.alert('電話番号の変更に失敗しました。');
      }
    } else {
      window.alert(
        `携帯電話番号変更可能制限に達しました。携帯電話番号を変更できません。\n市町村窓口にご連絡ください。\n${lgName}${contact.mainName}　${contact.mainTel}\nメールアドレス：${contact.mainMail}`
      );
    }
  };

  /**
   * 再送時の文言の変化
   * @returns 再送ボタン部分の文言
   */
  const howToSend = () => {
    const wordsResend = [];
    if (sendType === 1 || sendType === 3) {
      wordsResend.push(
        <p className="fs_14px txtCenter mb0" key="notMail">
          <a
            className="resendMailLink"
            role="button"
            tabIndex={0}
            style={{ textDecoration: 'underline' }}
            onClick={() => {
              infoMailRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
              });
            }}
          >
            メールが届かない場合はこちらから再送してください。
          </a>
        </p>
      );
    }
    if (sendType === 2 || sendType === 3) {
      wordsResend.push(
        <p className="fs_14px txtCenter mb40" key="notSms">
          <a
            className="resendSmsLink"
            role="button"
            tabIndex={0}
            style={{ textDecoration: 'underline' }}
            onClick={() => {
              infoSmsRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
              });
            }}
          >
            SMSが届かない場合はこちらから再送してください。
          </a>
        </p>
      );
    }
    return wordsResend;
  };

  /**
   * SMSを送信するときの文言
   * @returns SMS送信有無の文言
   */
  const changeWord = () => {
    const wordsChange = [];
    if (sendType === 2 || sendType === 3) {
      wordsChange.push(
        <dl key="SMS">
          <dt>SMS</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>送信する</dd>
          <dt>SMSを送信した携帯電話番号</dt>
          <dd>{smsTelNum}</dd>
        </dl>
      );
    }
    if (sendType === 1 || sendType === 3) {
      wordsChange.push(
        <dl key="mail">
          <dt>メール</dt>
          <dd style={{ overflowWrap: 'anywhere' }}>{mailAddress}</dd>
        </dl>
      );
    }
    return wordsChange;
  };
  /**
   * 再送時の文言の変化
   * @returns 再送ボタン周辺の文言
   */
  const resendAndChange = () => {
    const changeModes = [];
    if (sendType === 1 || sendType === 3) {
      changeModes.push(
        <React.Fragment key="resend_mail">
          <div id="infoMail" className="inner450">
            <h3 className="fs_15px txtCenter mb40">予約完了メールの受信を確認してください。</h3>
            <p>
              メールが届かない場合は、{fromMailAddress}
              からの 受信許可を確認いただき、再送ボタンを押してください。
            </p>
            <p className="txtCenter">
              <a
                role="button"
                // href="javascript:void(0);"
                className="btn btMail"
                onClick={() => onClickResendMail()}
                tabIndex={0}
              >
                メールを再送する
              </a>
            </p>
          </div>
          <dl className="row wrap dlrowlist">
            <dt />
            <dd>
              <p>メールアドレスを変更する場合は下記にて変更してください。</p>
            </dd>
            <dt>
              メール&nbsp;<span className="required">*必須</span>
            </dt>
            <dd>
              <input
                type="email"
                name="email"
                style={{ width: '100%' }}
                value={newMailAddress}
                onChange={(e) => setNewMailAddress(e.target.value)}
              />
            </dd>
            <dt>
              メール&nbsp;<span>（確認用）</span>
            </dt>
            <dd>
              <input
                type="email"
                name="email2"
                style={{ width: '100%' }}
                value={newConfMailAddress}
                onChange={(e) => setNewConfMailAddress(e.target.value)}
              />
            </dd>
          </dl>
          <p className="mb40">
            <a
              role="button"
              // href="javascript:void(0);"
              className="btn btMail"
              onClick={() => onClickUpdateMail()}
              tabIndex={0}
            >
              メールアドレスを変更して再送する
            </a>
          </p>
          <hr ref={infoMailRef} />
        </React.Fragment>
      );
    }
    if (sendType === 2 || sendType === 3) {
      changeModes.push(
        <React.Fragment key="resend_sms">
          <div id="infoSMS" className="inner450">
            <h3 className="fs_15px txtCenter mb40">予約完了SMSの受信を確認してください。</h3>
            <p>
              SMSが届かない場合は、携帯電話でSMSの受信許可をご確認いただき、再送ボタンを押してください。
            </p>
            <p className="txtCenter">
              <a
                role="button"
                // href="javascript:void(0);"
                className="btn btMail"
                onClick={() => onClickResendSms()}
                tabIndex={0}
              >
                SMSを再送する
              </a>
            </p>
          </div>
          <dl className="row wrap dlrowlist">
            <dt />
            <dd>
              <p>携帯電話番号を変更する場合は下記にて変更してください。</p>
            </dd>
            <dt>
              携帯電話番号&nbsp;<span className="required">*必須</span>
            </dt>
            <dd>
              <input
                type="tel"
                name="newTelNumber"
                style={{ width: '100%' }}
                value={newTelNum}
                onChange={(e) => setNewTelNum(e.target.value)}
              />
            </dd>
          </dl>
          <p className="mb40">
            <a
              role="button"
              // href="javascript:void(0);"
              className="btn btMail"
              onClick={() => onClickUpdateSms()}
              tabIndex={0}
            >
              電話番号を変更して再送する
            </a>
          </p>
          <hr ref={infoSmsRef} />
        </React.Fragment>
      );
    }
    return changeModes;
  };

  // 住民情報を表示する。
  const makeReserveCompleteDetail = () => {
    const displayCompleteDetail = [];
    for (let i = 0; i < customizeData.length; i += 1) {
      const { question = '', answer = [], inputType = 1 } = customizeData[i];
      if (answer[0] !== '' && answer.length !== 0) {
        displayCompleteDetail.push(
          <dl key={`question_num_${i + 1}`}>
            <dt>{question}</dt>
            <dd className="textCnfCmp characterDown">
              {inputType === INPUT_DATE_FLAG || inputType === INPUT_TYPE_BIRTHDAY
                ? dayjs(answer[0]).format('YYYY[/]MM[/]DD')
                : answer.join(',')}
            </dd>
          </dl>
        );
      }
    }

    return displayCompleteDetail;
  };

  return (
    <>
      <TopicPath />
      <main>
        <article>
          <div id="contents">
            <MoveTop lgCd={lgCd} />
            <div className="inner">
              <h2>
                予約が完了しました。
                <br />
                {sendType === 1 ? <>メールを送信しましたので確認してください。</> : null}
                {sendType === 2 ? <>SMSを送信しましたので確認してください。</> : null}
                {sendType === 3 ? <>メール、SMSを送信しましたので確認してください。</> : null}
              </h2>
              {howToSend()}
              <p className="txtCenter" style={{ fontSize: '20px' }}>
                <strong>予約番号</strong>
              </p>
              <div id="reservation" className="mt0">
                {rsvNumber}
              </div>
              <p className="txtCenter">
                <strong style={{ fontSize: '20px' }}>予約内容</strong>
              </p>
              <table className="tbl_confirmation noBorder mt0">
                <tbody>
                  <tr>
                    <th>利用場所</th>
                    {useMethod ? (
                      <td>{useMethod}</td>
                    ) : (
                      <td>
                        {placeName}
                        {placeName && placeAddress ? <br /> : null}
                        {`${placeAddress} ${placeSubAddress}`}
                        {placeAddress && placeTel ? <br /> : null}
                        {placeTel ? `TEL: ${placeTel}` : ''}
                        <br />
                        {rsvFlag === RSV_FLAG_PHONE ? dispTellStartPeople(tellStart) : null}
                      </td>
                    )}
                  </tr>
                  <tr>
                    <th>利用日時</th>
                    <td>{dayjs(rsvDate).format('YYYY[年]M[月]D[日](ddd) H[時]mm[分]')}</td>
                  </tr>
                  {checkedRecItems.length > 0 ? (
                    <tr>
                      <th>用件</th>
                      <td>{displayRecItems()}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
              <p className="txtCenter">
                <strong>予約者様の情報</strong>
              </p>
              <div className="customInputData">
                {makeReserveCompleteDetail()}
                {numOfAttendees > 0 ? (
                  <dl>
                    <dt>同席者数</dt>
                    <dd>{numOfAttendees}人</dd>
                  </dl>
                ) : null}
                <dl>
                  <dt>備考</dt>
                  <dd style={{ overflowWrap: 'anywhere' }} className="textDown characterDown">
                    {note}
                  </dd>
                </dl>
                {changeWord()}
              </div>
              {rsvFlag === 0 ? (
                <div style={{ margin: '30px 0', textAlign: 'center', fontSize: '14px' }}>
                  完了通知に記載されているURLは、利用日時の約1週間後まで有効です
                </div>
              ) : null}
              <hr />
              {resendAndChange()}
              <p id="navi">
                <a
                  role="button"
                  // href="javascript:void(0);"
                  onClick={() => navigate(`/${lgCd}`)}
                  className="btn btBack ma"
                  tabIndex={0}
                >
                  TOP
                </a>
              </p>
            </div>
            <div className="cancelAnnounce">{CANCEL_ANNOUNCE}</div>
          </div>
        </article>
      </main>
    </>
  );
}

export default CustomizeComplete;
